import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, X, Expand } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const units = [
  {
    id: "razor",
    src: "/images/k9-razor.jpeg",
    title: "Unit K-7 'Razor'",
    specs: ["Belgian Malinois", "Cert: Level III Dual-Purpose", "Handler: J. Okafor", "Radius: 2.5 km"],
  },
  {
    id: "onyx",
    src: "/images/k9-onyx.jpeg",
    title: "Unit K-3 'Onyx'",
    specs: ["Belgian Malinois", "Cert: Tracking & Detection", "Handler: M. Reyes", "Terrain Ops"],
  },
  {
    id: "havoc",
    src: "/images/k9-havoc.jpeg",
    title: "Unit K-9 'Havoc'",
    specs: ["German Shepherd", "Cert: Compound Free-Run", "Handler: T. Banda", "Radius: 4.0 km"],
  },
  {
    id: "takedown",
    src: "/images/k9-takedown.jpeg",
    title: "Bite Work & Decoy Drills",
    specs: ["Controlled Engagement", "Decoy Takedown Training", "Weekly Handler Certification"],
  },
  {
    id: "transport",
    src: "/images/k9-transport.jpeg",
    title: "Rapid Deployment Team",
    specs: ["Dual K9 Transport", "24/7 Standby Crew", "Response <15 Min"],
  },
  {
    id: "optics",
    src: "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&q=85",
    title: "Optics Node 04",
    specs: ["Panomera 180° Array", "Thermal Overlay", "AI Motion Flagging"],
  },
];

export default function Gallery() {
  const [nightVision, setNightVision] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className={`relative py-24 sm:py-32 ${nightVision ? "night-vision" : ""}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            testid="gallery-heading"
            overline="Field Assets"
            title="K9 Units & Operatives"
            description="The teams and hardware currently holding perimeters across logistics parks, compounds and executive estates."
          />
          <button
            data-testid="night-vision-toggle"
            onClick={() => setNightVision(!nightVision)}
            className={`clip-corners font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 mb-14 flex items-center gap-2 border transition-colors duration-300 ${
              nightVision ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-300" : "border-white/20 text-gray-300 hover:border-blue-500/50 hover:text-blue-400"
            }`}
          >
            <Eye className="w-4 h-4" />
            {nightVision ? "Night Vision: On" : "Night Vision: Off"}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {units.map((u, i) => (
            <Reveal key={u.id} delay={(i % 3) * 0.08}>
              <figure
                data-testid={`gallery-item-${u.id}`}
                className="group relative clip-frame overflow-hidden border border-white/10 hover:border-blue-500/40 transition-colors duration-500 cursor-pointer"
                onClick={() => setLightbox(u)}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={u.src}
                    alt={u.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#08090B] via-transparent to-transparent" />
                <Expand className="absolute top-4 right-4 w-5 h-5 text-white/60 group-hover:text-blue-400 transition-colors duration-300" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-display font-bold text-lg text-gray-100 uppercase tracking-tight">{u.title}</p>
                  <ul className="mt-2 space-y-1 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    {u.specs.map((s) => (
                      <li key={s} className="font-mono text-[11px] uppercase tracking-[0.15em] text-gray-300 flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500" /> {s}
                      </li>
                    ))}
                  </ul>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#08090B]/95 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.src} alt={lightbox.title} className="w-full clip-frame border border-white/10 max-h-[75vh] object-cover" />
              <div className="mt-4 flex items-center justify-between">
                <p className="font-display font-bold text-xl uppercase">{lightbox.title}</p>
                <button data-testid="lightbox-close-button" onClick={() => setLightbox(null)} className="text-gray-400 hover:text-blue-500">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
